import { create } from "zustand";

// ===== Global Radio Player Store =====
// Uses Zustand so state survives page navigation
// Audio element is created once and persists

let audioElement = null;

function getAudio() {
  if (!audioElement) {
    audioElement = new Audio();
    audioElement.preload = "auto";
  }
  return audioElement;
}

export const useRadioStore = create((set, get) => ({
  // Playback state
  playing: false,
  loading: false,
  currentStation: null,
  currentTrack: null,
  currentPlan: null,
  currentSegmentIndex: 0,
  volume: 0.8,
  muted: false,
  visible: false,

  // Now playing from Firestore
  nowPlayingData: null,

  // Initialize audio listeners
  _initialized: false,
  init() {
    if (get()._initialized) return;
    const audio = getAudio();
    audio.volume = get().volume;

    audio.addEventListener("ended", () => {
      // Auto-advance to next segment in plan
      const { currentPlan, currentSegmentIndex } = get();
      if (currentPlan?.segments?.length > currentSegmentIndex + 1) {
        get().playSegment(currentSegmentIndex + 1);
      } else {
        set({ playing: false, currentTrack: null });
      }
    });

    audio.addEventListener("playing", () => set({ playing: true, loading: false }));
    audio.addEventListener("waiting", () => set({ loading: true }));
    audio.addEventListener("canplay", () => set({ loading: false }));
    audio.addEventListener("error", (e) => {
      console.warn("Radio audio error:", e);
      set({ loading: false, playing: false });
    });

    set({ _initialized: true });
  },

  async playStation(station, plan) {
    get().init();
    const audio = getAudio();

    set({ currentStation: station, currentPlan: plan || null, visible: true, loading: true });

    if (plan?.segments?.length > 0) {
      get().playSegment(0);
    } else if (station?.streamURL) {
      audio.src = station.streamURL;
      audio.load();
      try {
        await audio.play();
        set({ playing: true, loading: false });
      } catch (e) {
        console.warn("Playback blocked:", e);
        set({ playing: false, loading: false });
      }
    } else {
      set({ loading: false });
    }
  },

  async playSegment(index) {
    get().init();
    const { currentPlan } = get();
    const segment = currentPlan?.segments?.[index];
    if (!segment?.mp3FileUrl) return;

    const audio = getAudio();
    audio.src = segment.mp3FileUrl;
    audio.load();
    try {
      await audio.play();
      set({
        playing: true,
        loading: false,
        currentSegmentIndex: index,
        currentTrack: {
          title: segment.title || "Unknown Track",
          artist: segment.artist || "",
          artworkUrl: segment.artworkUrl || null,
          durationSeconds: segment.durationSeconds || 0,
          showTitle: segment.showTitle || null,
          segmentType: segment.segmentType || "track",
        },
      });
    } catch (e) {
      console.warn("Segment playback error:", e);
      set({ loading: false });
    }
  },

  async playTrack(track) {
    get().init();
    if (!track?.mp3FileUrl && !track?.storageURL) return;
    const url = track.mp3FileUrl || track.storageURL;
    const audio = getAudio();
    audio.src = url;
    audio.load();
    try {
      await audio.play();
      set({
        playing: true,
        loading: false,
        visible: true,
        currentTrack: {
          title: track.title || "Unknown Track",
          artist: track.artist || "",
          artworkUrl: track.artworkUrl || null,
          durationSeconds: track.durationSeconds || 0,
        },
      });
    } catch (e) {
      console.warn("Track playback error:", e);
    }
  },

  async togglePlay() {
    get().init();
    const audio = getAudio();
    const { playing } = get();
    if (playing) {
      audio.pause();
      set({ playing: false });
    } else {
      try {
        await audio.play();
        set({ playing: true });
      } catch (e) {
        console.warn("Resume failed:", e);
      }
    }
  },

  setVolume(v) {
    const audio = getAudio();
    audio.volume = v;
    set({ volume: v, muted: v === 0 });
  },

  toggleMute() {
    const { muted, volume } = get();
    const audio = getAudio();
    if (muted) {
      audio.volume = volume || 0.8;
      set({ muted: false });
    } else {
      audio.volume = 0;
      set({ muted: true });
    }
  },

  stop() {
    const audio = getAudio();
    audio.pause();
    audio.src = "";
    set({ playing: false, visible: false, currentStation: null, currentTrack: null, currentPlan: null });
  },

  setNowPlaying(data) {
    set({ nowPlayingData: data });
    if (data?.currentTrackTitle) {
      set((s) => ({
        currentTrack: {
          ...s.currentTrack,
          title: data.currentTrackTitle,
          artist: data.currentTrackArtist || "",
          artworkUrl: data.currentArtworkUrl || s.currentTrack?.artworkUrl,
        },
      }));
    }
  },
}));
